import React, { useState, useEffect } from 'react';
import { api, getCachedUser } from '../api';
import { todayISO } from '../dateUtils';
import { refreshCache } from '../store';

export default function TransactionForm({ transaction, categories, periodId, presetType, onSave, onCancel }) {
  const [type, setType] = useState(presetType || transaction?.type || 'expense');
  const [categoryId, setCategoryId] = useState(transaction?.categoryId || '');
  const [amount, setAmount] = useState(transaction?.amount != null ? transaction.amount : '');
  const [date] = useState(transaction?.date || todayISO());
  const [description, setDescription] = useState(transaction?.description || '');
  const [localCategories, setLocalCategories] = useState(categories || []);
  const [accounts, setAccounts] = useState([]);
  const [debtAccounts, setDebtAccounts] = useState([]);
  const [accountId, setAccountId] = useState(transaction?.accountId || '');
  const [toAccountId, setToAccountId] = useState(transaction?.toAccountId || '');
  const [debtAccountId, setDebtAccountId] = useState(transaction?.debtAccountId || '');
  const [debtKind, setDebtKind] = useState(transaction?.debtKind || 'lend');
  const [errors, setErrors] = useState([]);
  const [addingCategory, setAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  const userId = getCachedUser()?.id;
  const lastAccountKey = userId ? `kopilka_lastAccount_${userId}` : null;

  // Если категории не передали пропсом (например, форма из FAB) — подгружаем сами
  useEffect(() => {
    if (!localCategories || localCategories.length === 0) {
      api.getCategories().then(cats => setLocalCategories(cats || []));
    }
  }, []);

  useEffect(() => {
    api.getAccounts().then(accs => {
      const regular = (accs || []).filter(a => !a.isDebt);
      const debts = (accs || []).filter(a => a.isDebt);
      setAccounts(regular);
      setDebtAccounts(debts);
      // По умолчанию — последний использованный счёт (запоминается между операциями)
      const last = lastAccountKey ? localStorage.getItem(lastAccountKey) : null;
      const validLast = regular.find(a => String(a.id) === String(last));
      if (transaction?.accountId) {
        setAccountId(transaction.accountId);
      } else if (validLast) {
        setAccountId(validLast.id);
      } else if (regular.length > 0) {
        setAccountId(regular[0].id);
      }
    });
  }, [lastAccountKey, transaction?.accountId]);

  const handleAccountChange = (e) => {
    const val = e.target.value;
    setAccountId(val);
    // Запоминаем выбранный счёт для следующей операции
    if (lastAccountKey) localStorage.setItem(lastAccountKey, val);
  };

  const handleCategoryChange = (e) => {
    const val = e.target.value;
    if (val === '__add_new__') {
      setAddingCategory(true);
    } else {
      setCategoryId(val);
      setAddingCategory(false);
    }
  };

  const addNewCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) return;
    const created = await api.createCategory({ name, type });
    setLocalCategories(prev => [...prev, created]);
    setCategoryId(created.id);
    setNewCategoryName('');
    setAddingCategory(false);
    refreshCache();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = [];
    const num = parseFloat(amount);
    if (!type) errs.push('Выберите тип операции');
    if (isNaN(num) || num <= 0) errs.push('Сумма должна быть числом больше 0');
    if (!date) errs.push('Укажите дату операции');

    if (type === 'transfer') {
      if (!accountId) errs.push('Выберите счёт, с которого переводите');
      if (!toAccountId) errs.push('Выберите счёт, на который переводите');
      if (accountId && toAccountId && accountId === toAccountId) errs.push('Счета должны отличаться');
    } else if (type === 'debt') {
      if (!debtAccountId) errs.push('Выберите человека (долг)');
      if (!accountId) errs.push('Выберите счёт');
    } else {
      if (!accountId) errs.push('Выберите счёт');
    }

    if (errs.length > 0) { setErrors(errs); return; }

    setErrors([]);

    const baseData = {
      type,
      categoryId: type === 'income' || type === 'expense' ? (parseInt(categoryId) || null) : null,
      amount: num,
      date,
      description,
      periodId: periodId ? parseInt(periodId) : null,
      accountId: accountId ? parseInt(accountId) : null,
      toAccountId: type === 'transfer' ? (parseInt(toAccountId) || null) : null,
      debtAccountId: type === 'debt' ? (parseInt(debtAccountId) || null) : null,
      debtKind: type === 'debt' ? debtKind : null,
    };

    onSave(baseData);
  };

  const filteredCategories = (type === 'income' || type === 'expense')
    ? localCategories.filter(c => c.type === type)
    : [];

  return (
    <>
    <form className="card" onSubmit={handleSubmit}>
      <h3>{transaction ? 'Редактировать транзакцию' : 'Новая транзакция'}</h3>
      {errors.length > 0 && (
        <div className="form-error">
          {errors.map((er, i) => <div key={i}>{er}</div>)}
        </div>
      )}
      {!presetType && (
        <div className="form-group">
          <label>Тип</label>
          <select value={type} onChange={(e) => setType(e.target.value)}>
            <option value="expense">Расход</option>
            <option value="income">Доход</option>
            <option value="transfer">Перевод</option>
            <option value="debt">Долг</option>
          </select>
        </div>
      )}

      {type === 'transfer' && (
        <>
          <div className="form-group">
            <label>Со счёта</label>
            <select value={accountId} onChange={handleAccountChange} required>
              <option value="">Выберите счёт</option>
              {accounts.map(a => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>На счёт</label>
            <select value={toAccountId} onChange={(e) => setToAccountId(e.target.value)} required>
              <option value="">Выберите счёт</option>
              {accounts.map(a => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
        </>
      )}

      {type === 'debt' && (
        <>
          <div className="form-group">
            <label>Человек (долг)</label>
            <select value={debtAccountId} onChange={(e) => setDebtAccountId(e.target.value)} required>
              <option value="">Выберите человека</option>
              {debtAccounts.map(a => (
                <option key={a.id} value={a.id}>{a.person || a.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Операция</label>
            <select value={debtKind} onChange={(e) => setDebtKind(e.target.value)}>
              <option value="lend">Дал в долг</option>
              <option value="borrow">Взял в долг</option>
              <option value="return">Вернул долг</option>
              <option value="collect">Получил обратно</option>
            </select>
          </div>
          <div className="form-group">
            <label>Счёт</label>
            <select value={accountId} onChange={handleAccountChange} required>
              <option value="">Выберите счёт</option>
              {accounts.map(a => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
        </>
      )}

      {/* Сумма — первым полем, сразу в фокусе (цифровая клавиатура) */}
      <div className="form-group">
        <label>Сумма</label>
        <div className="amount-field">
          <input
            type="number"
            inputMode="decimal"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
            placeholder="0"
            autoFocus
          />
          <span className="amount-field-currency">₽</span>
        </div>
      </div>

      {(type === 'expense' || type === 'income') && (
        <>
          <div className="form-group">
            <label>Счёт</label>
            <select value={accountId} onChange={handleAccountChange} required>
              <option value="">Выберите счёт</option>
              {accounts.map(a => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Категория (необязательно)</label>
            {addingCategory ? (
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  placeholder="Название новой категории"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addNewCategory(); } }}
                />
                <button type="button" onClick={addNewCategory}>OK</button>
                <button type="button" className="secondary" onClick={() => setAddingCategory(false)}>✕</button>
              </div>
            ) : (
              <select value={categoryId} onChange={handleCategoryChange}>
                <option value="">Без категории</option>
                {filteredCategories.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
                <option value="__add_new__">➕ Добавить новую</option>
              </select>
            )}
          </div>
        </>
      )}
      <div className="form-group">
        <label>Описание</label>
        <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} />
      </div>

      <div style={{ display: 'flex', gap: '8px' }}>
        <button type="submit">Сохранить</button>
        <button type="button" className="secondary" onClick={onCancel}>Отмена</button>
      </div>
    </form>
    </>
  );
}
